/**
 * NavGroup — a collapsible sidebar section with sub-items.
 *
 * Extracted from the DMARC accordion, which was hand-rolled inline in Sidebar.tsx. It
 * was already the app's pattern for "one entry, several destinations"; the Intelligence
 * hub introduced a competing one (a tab strip) instead of reusing it, which left the app
 * with two navigation systems for the same tree. This is the one that stays.
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';

/** Mono glyph used as the nav item marker, matching NavItem. */
const NAV_GLYPH = '><>';

interface NavGroupProps {
  label: string;
  /**
   * True when the current route belongs to this group — drives both the active styling
   * and whether the group starts open.
   */
  isActive: (pathname: string) => boolean;
  children: ReactNode;
}

export function NavGroup({ label, isActive, children }: NavGroupProps) {
  const location = useLocation();
  const active = isActive(location.pathname);
  const [open, setOpen] = useState(active);

  // Deep-linking into a group (from a dossier, global search, or a bookmark) must open
  // it, otherwise the sidebar shows a collapsed group as the active one and the user
  // cannot see where they are. Only forces open — never collapses a group the user
  // opened by hand.
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={`flex items-center gap-3 h-[38px] px-3 w-full text-left font-mono text-[13px]
          rounded-phish-0 border-l-2 transition-colors duration-phish ${
            active
              ? 'bg-phish-bg-3 text-phish-fg-1 border-l-phish-accent'
              : 'bg-transparent text-phish-fg-2 border-l-transparent hover:bg-white/5'
          }`}
      >
        <span className={active ? 'text-phish-accent' : 'text-phish-fg-3'} aria-hidden>
          {NAV_GLYPH}
        </span>
        <span className="flex-1">{label}</span>
        {open ? (
          <ChevronDown size={16} strokeWidth={1.5} className="text-phish-fg-3" />
        ) : (
          <ChevronRight size={16} strokeWidth={1.5} className="text-phish-fg-3" />
        )}
      </button>
      {open && <div className="py-1">{children}</div>}
    </>
  );
}

export default NavGroup;
